import React, { Fragment, Suspense, useEffect, useMemo, useState } from "react";
import { Route, Switch } from "react-router";
import { importOptional, lazyWithRetry } from "../core/lazy_with_retry";
import { AuthPortalCustomLoginProvider } from "./auth_portal_custom_login_provider";
import { PartnerConfig } from "./partner_config";
import { usePartnerThemeCss } from "./use_partner_theme_css";
import { PortalHomePage } from "./portal/pages/portal_home_page";
import { AppTracker } from "../core/app_tracker";
import { PageLoader } from "../components/page_loader";
import { usePartnerQuery } from "./api";
import { PortalMembershipPage } from "./portal/pages/portal_membership_page";
import { PortalMeetingRoomPage } from "./portal/pages/portal_meeting_room_page";
import { PortalManageMembershipPage } from "./portal/pages/portal_manage_membership_page";
import { getPortalPaths } from "./portal/hooks/use_portal_history";
import { portalAuthPaths, SIGNUP_SCREEN } from "./portal_paths";
import { PortalAllBookingsPage } from "./portal/pages/portal_all_bookings_page";
import { useAnalytics } from "@/lib/analytics/analytics";
import { PortalLoginPage } from "./portal/pages/portal_login_page";
import { PortalCallbackPage } from "./portal_callback_page";
import { AuthPortalProvider } from "./auth_portal_provider";
import { AuthPortalSWRClient } from "./auth_swr_client";
import { portalAuth } from "./portal_auth_config";
import { useConfig } from "@/core/config";
import { PortalSignupPage } from "./portal/pages/portal_signup_page";
import { usePortalPartnerSlug } from "./use_portal_partner_slug";
import { usePortalSignedOutScreen } from "./use_portal_signed_out_screen";
import { useCanonicalPortalUrl } from "./use_canonical_portal_url";

export default function PortalPagesWrapper() {
  const config = useConfig();
  const partnerSlug = usePortalPartnerSlug();
  const signedOutScreen = usePortalSignedOutScreen();
  useCanonicalPortalUrl();

  // One lazy component per partner. Building it during render would hand React a
  // new component type every pass, and a never-before-rendered lazy type suspends
  // — to the app-level boundary, wiping the shell mid-login.
  const Theme = useMemo(
    () =>
      lazyWithRetry(() =>
        importOptional(import(`./${partnerSlug}/${partnerSlug}_theme.tsx`))
      ),
    [partnerSlug]
  );

  const partner = usePartnerQuery(partnerSlug);
  const analytics = useAnalytics();
  usePartnerThemeCss(partnerSlug);

  useEffect(() => {
    analytics.register({ Source: "Portal" });
  }, [analytics]);

  const [partnerConfig, setPartnerConfig] = useState<PartnerConfig>();
  import(`./${partnerSlug}/${partnerSlug}_partner_config.tsx`).then(
    (partnerConfig) => {
      setPartnerConfig(partnerConfig.partnerConfig);
    }
  );

  useEffect(() => {
    if (partner?.name) {
      analytics.register({ "Partner Name": partner.name });
    }
  }, [analytics, partner?.name]);

  const portalAuthConfigs = portalAuth(partnerSlug);
  const currentPortalAuthConfig = portalAuthConfigs[config.environment];

  if (!partnerConfig || !currentPortalAuthConfig) {
    return null;
  }

  const portalPath = getPortalPaths();

  return (
    // The partner theme is a lazy component, and a lazy type suspends the first
    // time it renders. Without a boundary here that reaches the app-level one and
    // wipes the whole shell, including the form being filled in.
    <Suspense fallback={<PageLoader />}>
      <AuthPortalCustomLoginProvider partnerSlug={partnerSlug}>
        <AuthPortalProvider
          clientId={currentPortalAuthConfig.clientId}
          domain={currentPortalAuthConfig.domain}
          audience={currentPortalAuthConfig.audience}
          redirectUri={currentPortalAuthConfig.redirectUri}
          signedOut={
            <Fragment>
              <Theme />
              {signedOutScreen === SIGNUP_SCREEN ? (
                <PortalSignupPage partnerConfig={partnerConfig} />
              ) : (
                <PortalLoginPage partnerConfig={partnerConfig} />
              )}
            </Fragment>
          }
        >
          <AuthPortalSWRClient>
            <Switch>
              <Route exact path={portalAuthPaths.callback}>
                <PortalCallbackPage />
              </Route>
              <Route path={portalPath.meetingRooms}>
                <PortalMeetingRoomPage
                  partnerConfig={partnerConfig}
                  partner={partner}
                />
              </Route>
              <Route path={portalPath.manageMembership}>
                <PortalManageMembershipPage
                  partnerConfig={partnerConfig}
                  partner={partner}
                />
              </Route>
              <Route path={portalPath.membership}>
                <PortalMembershipPage
                  partnerConfig={partnerConfig}
                  partner={partner}
                />
              </Route>
              <Route path={portalPath.allBookings}>
                <PortalAllBookingsPage
                  partnerConfig={partnerConfig}
                  partner={partner}
                />
              </Route>
              <Route exact path={portalPath.home}>
                <PortalHomePage
                  partnerConfig={partnerConfig}
                  partner={partner}
                />
              </Route>
            </Switch>
            <AppTracker />
          </AuthPortalSWRClient>
        </AuthPortalProvider>
      </AuthPortalCustomLoginProvider>
    </Suspense>
  );
}
